import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

import completeSeedGuideImage from "./assets/images/CompleteSeedGuide.jpg";
import completeSeedGuide from "./assets/pdfs/catalogues/CompleteSeedGuide.pdf";

import Bonnie from "./assets/pdfs/maizevarieties/Bonnie.pdf";
import Crosbey from "./assets/pdfs/maizevarieties/Crosbey.pdf";
import Emeleen from "./assets/pdfs/maizevarieties/Emeleen.pdf";
import EsConstance from "./assets/pdfs/maizevarieties/Es Constance.pdf";
import EsLegolas from "./assets/pdfs/maizevarieties/Es Legolas.pdf";
import EsLovely from "./assets/pdfs/maizevarieties/Es Lovely.pdf";
import EsMetronom from "./assets/pdfs/maizevarieties/Es Metronom.pdf";
import EsMyrdal from "./assets/pdfs/maizevarieties/Es Myrdal.pdf";
import EsPalladium from "./assets/pdfs/maizevarieties/Es Palladium.pdf";
import Faith from "./assets/pdfs/maizevarieties/Faith.pdf";
import Jakleen from "./assets/pdfs/maizevarieties/Jakleen.pdf";
import Justice from "./assets/pdfs/maizevarieties/Justice.pdf";
import Micheleen from "./assets/pdfs/maizevarieties/Micheleen.pdf";

const pdfs = [
  { name: "Faith", url: Faith },
  { name: "Es Lovely", url: EsLovely },
  { name: "Es Palladium", url: EsPalladium },
  { name: "Justice", url: Justice },
  { name: "Es Myrdal", url: EsMyrdal },
  { name: "Es Constance", url: EsConstance },
  { name: "Bonnie", url: Bonnie },
  { name: "Es Legolas", url: EsLegolas },
  { name: "Emeleen", url: Emeleen },
  { name: "Crosbey", url: Crosbey },
  { name: "Micheleen", url: Micheleen },
  { name: "Es Metronom", url: EsMetronom },
  { name: "Jakleen", url: Jakleen },
];

const MAIZE_GUIDES = {
  full: {
    url: "/2026MG.pdf",
    filename: "2026MG.pdf",
    label: "Full-quality version",
  },
  compressed: {
    url: "/2026MGc.pdf",
    filename: "2026MGc.pdf",
    label: "Compressed version",
  },
};

// A separate component keeps each download's state and cleanup isolated.
// Changing its key cancels the previous download and starts a fresh one.
function MaizeGuideContent({ version, onUseCompressed }) {
  const guide = MAIZE_GUIDES[version];

  const [pdfUrl, setPdfUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isTakingTooLong, setIsTakingTooLong] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    let objectUrl = "";

    const slowTimer = window.setTimeout(() => {
      if (active) {
        setIsTakingTooLong(true);
      }
    }, 10000);

    async function loadPdf() {
      try {
        const response = await fetch(guide.url, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`PDF request failed: ${response.status}`);
        }

        const blob = await response.blob();

        if (!active) {
          return;
        }

        // Catch missing-file responses that return the React HTML page
        // with a successful HTTP status instead of returning a PDF.
        const header = await blob.slice(0, 5).text();

        if (!active) {
          return;
        }

        if (header !== "%PDF-") {
          throw new Error("The server did not return a PDF file.");
        }

        // Ensure the browser recognises it as a PDF even if the server
        // sends a generic application/octet-stream content type.
        const pdfBlob = new Blob([blob], {
          type: "application/pdf",
        });

        objectUrl = URL.createObjectURL(pdfBlob);
        setPdfUrl(objectUrl);
      } catch (loadError) {
        if (!active || loadError.name === "AbortError") {
          return;
        }

        setError(
          "We couldn't load the guide. Please try opening it directly below."
        );
      } finally {
        window.clearTimeout(slowTimer);

        if (active) {
          setIsLoading(false);
        }
      }
    }

    loadPdf();

    return () => {
      active = false;
      controller.abort();
      window.clearTimeout(slowTimer);

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [guide.url]);

  return (
    <>
      <div className="mg-pdf-toolbar">
        <span className="mg-pdf-version">{guide.label}</span>

        <div className="mg-pdf-actions">
          <a
            href={guide.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open PDF directly
          </a>

          {!isLoading && pdfUrl && (
            <a href={pdfUrl} download={guide.filename}>
              Download PDF
            </a>
          )}
        </div>
      </div>

      <div className="mg-pdf-content" aria-busy={isLoading}>
        {isLoading && (
          <div className="mg-pdf-loading">
            <div className="mg-pdf-spinner" aria-hidden="true" />

            <p className="mg-pdf-status" role="status">
              {version === "compressed"
                ? "Loading the compressed guide…"
                : "Loading the 2026 Maize Guide…"}
            </p>

            <p className="mg-pdf-help">
              Your guide will appear here once it has downloaded.
            </p>

            {isTakingTooLong && version === "full" && (
              <div className="mg-pdf-slow-message" role="status">
                <p>Taking too long?</p>

                <button
                  type="button"
                  className="mg-pdf-text-button"
                  onClick={onUseCompressed}
                >
                  See our compressed version here
                </button>

                <p className="mg-pdf-help">
                  A smaller file for slower connections.
                </p>
              </div>
            )}

            {isTakingTooLong && version === "compressed" && (
              <p className="mg-pdf-help" role="status">
                Still downloading. Please wait, or use “Open PDF directly”
                above.
              </p>
            )}
          </div>
        )}

        {!isLoading && error && (
          <div className="mg-pdf-loading">
            <p className="mg-pdf-error" role="alert">
              {error}
            </p>

            <a
              className="mg-pdf-direct-link"
              href={guide.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open PDF directly
            </a>

            {version === "full" && (
              <button
                type="button"
                className="mg-pdf-text-button"
                onClick={onUseCompressed}
              >
                Try the compressed version
              </button>
            )}
          </div>
        )}

        {!isLoading && pdfUrl && (
          <iframe
            className="mg-pdf-frame"
            src={pdfUrl}
            title={`2026 Maize Guide — ${guide.label}`}
          />
        )}
      </div>

      <div className="mg-pdf-footer">
        If the PDF does not display on your device, use “Open PDF directly”
        above.
      </div>
    </>
  );
}

function MaizeGuideViewer({ onClose }) {
  const dialogRef = useRef(null);
  const [version, setVersion] = useState("full");

  useEffect(() => {
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;

    dialog.showModal();
    document.body.style.overflow = "hidden";

    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;

      if (
        previousFocus instanceof HTMLElement &&
        previousFocus.isConnected
      ) {
        previousFocus.focus();
      }
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className="mg-pdf-dialog"
      aria-labelledby="mg-pdf-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="mg-pdf-header">
        <h2 id="mg-pdf-title">2026 Maize Guide</h2>

        <button
          type="button"
          className="mg-pdf-close"
          onClick={onClose}
          aria-label="Close maize guide"
          autoFocus
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <MaizeGuideContent
        key={version}
        version={version}
        onUseCompressed={() => setVersion("compressed")}
      />
    </dialog>
  );
}

function Landing() {
  const [selectedPDFs, setSelectedPDFs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMaizeGuideOpen, setIsMaizeGuideOpen] = useState(false);

  const toggleSelection = (url) => {
    setSelectedPDFs((prev) =>
      prev.includes(url)
        ? prev.filter((pdf) => pdf !== url)
        : [...prev, url]
    );
  };

  const downloadSelected = () => {
    selectedPDFs.forEach((pdfUrl) => {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = pdfUrl.split("/").pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });

    setIsModalOpen(false);
    setSelectedPDFs([]);
  };

  return (
    <div className="landing-container">
      <img
        src="/Grainseed.jpg"
        alt="Banner Logo"
        className="bannerlogo"
      />

      <div className="shift-up">
        <div className="landing-text">
          <p>
            Grainseed is a leading supplier of high-quality agricultural
            products, trusted by farmers and growers across the UK. Our
            portfolio includes maize, grass, oilseed rape, sunflowers,
            cereals, fodder beet, forage crops, clamp covers, clamp
            additives, and fertiliser.
          </p>

          <p>
            With decades of experience in the seed industry, we combine
            technical expertise with a commitment to innovation. Our goal
            is to help you achieve the best possible results in your fields
            by providing reliable products and tailored advice.
          </p>

          <p>
            We pride ourselves on being able to source virtually any seed
            variety to meet your specific requirements.
          </p>
        </div>

        <button
          className="open-modal-btn"
          onClick={() => setIsModalOpen(true)}
        >
          View Maize Variety Guides
        </button>

        <button
          type="button"
          className="open-modal-btn"
          onClick={() => setIsMaizeGuideOpen(true)}
        >
          View 2026 Maize Guide
        </button>

        <div className="catalogue-images">
          <a
            href={completeSeedGuide}
            target="_blank"
            rel="noopener noreferrer"
            className="catalogue-image-link"
          >
            <img
              src={completeSeedGuideImage}
              alt="Complete Seed Guide"
              className="catalogue-image"
            />
          </a>
        </div>
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Select PDFs to Download</h2>

              <button
                className="close-modal-btn"
                onClick={() => setIsModalOpen(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <ul className="pdf-list">
              {pdfs.map((pdf) => (
                <li key={pdf.name}>
                  <label>
                    <input
                      type="checkbox"
                      onChange={() => toggleSelection(pdf.url)}
                      checked={selectedPDFs.includes(pdf.url)}
                    />
                    {pdf.name}
                  </label>
                </li>
              ))}
            </ul>

            <button
              className="download-btn"
              onClick={downloadSelected}
              disabled={selectedPDFs.length === 0}
            >
              Download Selected PDFs
            </button>
          </div>
        </div>
      )}

      {isMaizeGuideOpen && (
        <MaizeGuideViewer
          onClose={() => setIsMaizeGuideOpen(false)}
        />
      )}
    </div>
  );
}

export default Landing;
